import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plus, FileText, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface NDAVersion {
  id: string;
  version: string;
  name: string;
  active_from: string;
  active_until: string | null;
  created_at: string;
}

export function NDAVersionManager() {
  const [versions, setVersions] = useState<NDAVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newVersion, setNewVersion] = useState({
    name: '',
    version: '',
    content: '',
  });

  useEffect(() => {
    fetchVersions();
  }, []);

  const fetchVersions = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('legal_documents')
      .select('id, name, version, active_from, active_until, created_at')
      .eq('document_type', 'NDA')
      .order('created_at', { ascending: false });

    if (!error && data) {
      setVersions(data);
    }
    setLoading(false);
  };

  const handleCreateVersion = async () => {
    if (!newVersion.name || !newVersion.version || !newVersion.content) {
      toast.error('Please fill in all fields');
      return;
    }

    setCreating(true);
    try {
      const { error } = await supabase.functions.invoke('nda-create-version', {
        body: {
          name: newVersion.name,
          version: newVersion.version,
          content_md: newVersion.content,
        },
      });

      if (error) throw error;

      toast.success('New NDA version created. All users will need to re-accept.');
      setShowCreateDialog(false);
      setNewVersion({ name: '', version: '', content: '' });
      fetchVersions();
    } catch (error) {
      console.error('Create version error:', error);
      toast.error('Failed to create new NDA version');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create New Version
        </Button>
      </div>

      <div className="space-y-3">
        {versions.map((version) => (
          <Card key={version.id}>
            <CardContent className="flex items-center justify-between p-4">
              <div className="flex items-center gap-4">
                <FileText className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium">{version.name}</h4>
                    <Badge variant={version.active_until ? 'secondary' : 'default'}>
                      v{version.version}
                    </Badge>
                    {!version.active_until && <Badge>Active</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Created: {format(new Date(version.created_at), 'PPP')}
                    {version.active_until && (
                      <span className="ml-2">
                        • Retired: {format(new Date(version.active_until), 'PPP')}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <AlertDialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning" />
              Create New NDA Version
            </AlertDialogTitle>
            <AlertDialogDescription>
              Creating a new NDA version will retire the current version and require all
              investors/observers to re-accept the new terms before accessing company data.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="nda-name">Document Name</Label>
              <Input
                id="nda-name"
                placeholder="e.g., Standard NDA Agreement"
                value={newVersion.name}
                onChange={(e) => setNewVersion({ ...newVersion, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="nda-version">Version Number</Label>
              <Input
                id="nda-version"
                placeholder="e.g., 2.0"
                value={newVersion.version}
                onChange={(e) => setNewVersion({ ...newVersion, version: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="nda-content">Content (Markdown)</Label>
              <Textarea
                id="nda-content"
                placeholder="Enter NDA content in markdown format..."
                value={newVersion.content}
                onChange={(e) => setNewVersion({ ...newVersion, content: e.target.value })}
                rows={10}
                className="font-mono text-sm"
              />
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={creating}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleCreateVersion} disabled={creating}>
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Version'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
